// Standard cycling power metrics (Coggan / TrainingPeaks).

const ROLLING_WINDOW = 30;

export function computeNormalizedPower(powers) {
  const clean = powers.filter((p) => p != null && p >= 0);
  if (!clean.length) return null;
  if (clean.length < ROLLING_WINDOW) {
    return Math.round(clean.reduce((a, b) => a + b, 0) / clean.length);
  }

  const raised = [];
  for (let i = 0; i < clean.length; i++) {
    const start = Math.max(0, i - ROLLING_WINDOW + 1);
    const slice = clean.slice(start, i + 1);
    const avg = slice.reduce((a, b) => a + b, 0) / slice.length;
    raised.push(avg ** 4);
  }

  const mean = raised.reduce((a, b) => a + b, 0) / raised.length;
  return Math.round(mean ** 0.25);
}

/** Intensity Factor = NP / FTP */
export function computeIntensityFactor(np, ftp) {
  if (!np || !ftp) return null;
  return Math.round((np / ftp) * 1000) / 1000;
}

/** Training Stress Score */
export function computeTSS(durationSeconds, np, ftp) {
  if (!durationSeconds || !np || !ftp) return null;
  const intensityFactor = np / ftp;
  return Math.round((durationSeconds * np * intensityFactor) / (ftp * 3600) * 100);
}

/** Power-to-heart-rate efficiency (NP / avg HR). */
export function computeEfficiencyFactor(np, avgHr) {
  if (!np || !avgHr) return null;
  return Math.round((np / avgHr) * 100) / 100;
}

export function computePowerMetrics(samples, durationSeconds, ftp) {
  const powers = samples.map((s) => s.power);
  const hrs = samples.map((s) => s.hr).filter((h) => h != null);
  const avgHr = hrs.length
    ? Math.round(hrs.reduce((a, b) => a + b, 0) / hrs.length)
    : null;

  const np = computeNormalizedPower(powers);
  const intensityFactor = computeIntensityFactor(np, ftp);
  const tss = computeTSS(durationSeconds, np, ftp);
  const ef = computeEfficiencyFactor(np, avgHr);

  return {
    normalized_power: np,
    intensity_factor: intensityFactor,
    tss,
    efficiency_factor: ef,
  };
}

export function resolveActivityPowerMetrics(activity, userFtp = null) {
  if (!activity?.has_power) return null;

  const effectiveFtp = activity.effective_ftp_watts ?? userFtp ?? null;
  if (activity.normalized_power != null) {
    return {
      normalized_power: activity.normalized_power,
      intensity_factor:
        activity.intensity_factor ??
        computeIntensityFactor(activity.normalized_power, effectiveFtp),
      tss:
        activity.tss ??
        computeTSS(activity.duration_seconds, activity.normalized_power, effectiveFtp),
      efficiency_factor: null,
    };
  }

  const samples = activity.samples || [];
  const hasPowerSamples = samples.some((s) => s.power != null && s.power >= 0);
  if (!hasPowerSamples) return null;

  const np = computeNormalizedPower(samples.map((s) => s.power));
  if (np == null) return null;

  return {
    normalized_power: np,
    intensity_factor: computeIntensityFactor(np, effectiveFtp),
    tss: computeTSS(activity.duration_seconds, np, effectiveFtp),
    efficiency_factor: null,
  };
}

export function fmtIF(if_) {
  if (if_ == null) return "—";
  return `${Math.round(if_ * 100)}%`;
}

export function fmtEF(ef) {
  if (ef == null) return "—";
  return ef.toFixed(2);
}
